/**
 * Centralized SVG Icons Component Library
 * Usage: import { IconName } from '.components/SvgConsolidated/SvgIcons';
 * Then use: <IconName className="w-4 h-4" />
 */

import React from 'react';

interface IconProps {
  className?: string;
  fill?: string;
  stroke?: string;
  strokeWidth?: string | number;
}

// User Icon
export const UserIcon: React.FC<IconProps> = ({ className = "w-4 h-4", fill = "currentColor" }) => (
  <svg className={className} fill={fill} viewBox="0 0 24 24">
    <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
  </svg>
);

// Assistant/Bot Icon
export const AssistantIcon: React.FC<IconProps> = ({ className = "w-4 h-4", fill = "currentColor" }) => (
  <svg className={className} fill={fill} viewBox="0 0 24 24">
    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3 0 2-3 5.31-3 5.31S9 10 9 8c0-1.66 1.34-3 3-3zm0 2.5c-.83 0-1.5.67-1.5 1.5s.67 1.5 1.5 1.5 1.5-.67 1.5-1.5-.67-1.5-1.5-1.5z"/>
  </svg>
);

// Chat Icon
export const ChatIcon: React.FC<IconProps> = ({ className = "w-4 h-4", fill = "currentColor" }) => (
  <svg className={className} fill={fill} viewBox="0 0 24 24">
    <path d="M20 2H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h4v3c0 .6.4 1 1 1 .2 0 .3 0 .5-.1L14 18h6c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2z"/>
  </svg>
);

// Clock Icon
export const ClockIcon: React.FC<IconProps> = ({ className = "w-3 h-3", fill = "currentColor" }) => (
  <svg className={className} fill={fill} viewBox="0 0 20 20">
    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
  </svg>
);

// Close/X Icon
export const CloseIcon: React.FC<IconProps> = ({ className = "w-4 h-4", stroke = "currentColor", strokeWidth = 2 }) => (
  <svg className={className} fill="none" stroke={stroke} viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={strokeWidth} d="M6 18L18 6M6 6l12 12" />
  </svg>
);

// Edit/Pencil Icon
export const EditIcon: React.FC<IconProps> = ({ className = "w-5 h-5", stroke = "currentColor", strokeWidth = 2 }) => (
  <svg className={className} fill="none" stroke={stroke} viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={strokeWidth} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
  </svg>
);

// Delete/Trash Icon
export const DeleteIcon: React.FC<IconProps> = ({ className = "w-5 h-5", stroke = "currentColor", strokeWidth = 2 }) => (
  <svg className={className} fill="none" stroke={stroke} viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={strokeWidth} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
  </svg>
);

// Plus/Add Icon
export const PlusIcon: React.FC<IconProps> = ({ className = "w-4 h-4", stroke = "currentColor", strokeWidth = 2 }) => (
  <svg className={className} fill="none" stroke={stroke} viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={strokeWidth} d="M12 4v16m8-8H4" />
  </svg>
);

// Filter Icon
export const FilterIcon: React.FC<IconProps> = ({ className = "w-3.5 h-3.5", stroke = "currentColor", strokeWidth = 2 }) => (
  <svg className={className} fill="none" stroke={stroke} viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={strokeWidth} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
  </svg>
);

// Search Icon
export const SearchIcon: React.FC<IconProps> = ({ className = "w-5 h-5", stroke = "currentColor", strokeWidth = 2 }) => (
  <svg className={className} fill="none" stroke={stroke} viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={strokeWidth} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
  </svg>
);

// Microphone Icon
export const MicrophoneIcon: React.FC<IconProps> = ({ className = "w-8 h-8", fill = "currentColor" }) => (
  <svg className={className} fill={fill} viewBox="0 0 24 24">
    <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3z"/>
    <path d="M17 11c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z"/>
  </svg>
);

// Microphone Off Icon
export const MicrophoneOffIcon: React.FC<IconProps> = ({ className = "w-8 h-8", fill = "currentColor" }) => (
  <svg className={className} fill={fill} viewBox="0 0 24 24">
    <path d="M19 11h-1.7c0 .74-.16 1.43-.43 2.05l1.23 1.23c.56-.98.9-2.09.9-3.28zm-4.02.17c0-.06.02-.11.02-.17V5c0-1.66-1.34-3-3-3S9 3.34 9 5v.18l5.98 5.99zM4.27 3L3 4.27l6.01 6.01V11c0 1.66 1.33 3 2.99 3 .22 0 .44-.03.65-.08l1.66 1.66c-.71.33-1.5.52-2.31.52-2.76 0-5.3-2.1-5.3-5.1H5c0 3.41 2.72 6.23 6 6.72V21h2v-3.28c.91-.13 1.77-.45 2.54-.9L19.73 21 21 19.73 4.27 3z"/>
  </svg>
);

// Book Icon
export const BookIcon: React.FC<IconProps> = ({ className = "w-6 h-6", stroke = "currentColor", strokeWidth = 2 }) => (
  <svg className={className} fill="none" stroke={stroke} viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={strokeWidth} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
  </svg>
);

// Student Icon
export const StudentIcon: React.FC<IconProps> = ({ className = "w-6 h-6", stroke = "currentColor", strokeWidth = 2 }) => (
  <svg className={className} fill="none" stroke={stroke} viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={strokeWidth} d="M12 14l9-5-9-5-9 5 9 5z" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={strokeWidth} d="M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z" />
  </svg>
);

// Teacher Icon (graduation cap)
export const TeacherIcon: React.FC<IconProps> = ({ className = "w-8 h-8", fill = "currentColor" }) => (
  <svg className={className} fill={fill} viewBox="0 0 24 24">
    <path d="M12 3L1 9l4 2.18v6L12 21l7-3.82v-6L23 9l-11-6zM5 13.18l7 3.82 7-3.82V17L12 20.82 5 17v-3.82z"/>
  </svg>
);

// Activity Icon
export const ActivityIcon: React.FC<IconProps> = ({ className = "w-6 h-6", stroke = "currentColor", strokeWidth = 2 }) => (
  <svg className={className} fill="none" stroke={stroke} viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={strokeWidth} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
  </svg>
);

// Settings Icon
export const SettingsIcon: React.FC<IconProps> = ({ className = "w-6 h-6", stroke = "currentColor", strokeWidth = 2 }) => (
  <svg className={className} fill="none" stroke={stroke} viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={strokeWidth} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={strokeWidth} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
  </svg>
);

// Upload Icon
export const UploadIcon: React.FC<IconProps> = ({ className = "w-4 h-4", stroke = "currentColor", strokeWidth = 2 }) => (
  <svg className={className} fill="none" stroke={stroke} viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={strokeWidth} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v10" />
  </svg>
);

// Download Icon
export const DownloadIcon: React.FC<IconProps> = ({ className = "w-5 h-5", stroke = "currentColor", strokeWidth = 2 }) => (
  <svg className={className} fill="none" stroke={stroke} viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={strokeWidth} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
  </svg>
);

// Checkmark Icon
export const CheckmarkIcon: React.FC<IconProps> = ({ className = "w-5 h-5", stroke = "currentColor", strokeWidth = 3 }) => (
  <svg className={className} fill="none" stroke={stroke} strokeWidth={strokeWidth} viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
  </svg>
);

// Warning Icon
export const WarningIcon: React.FC<IconProps> = ({ className = "w-5 h-5", fill = "currentColor" }) => (
  <svg className={className} viewBox="0 0 20 20" fill={fill}>
    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
  </svg>
);

// Info Icon
export const InfoIcon: React.FC<IconProps> = ({ className = "w-4 h-4", fill = "currentColor", stroke = "none" }) => (
  <svg className={className} fill={fill} stroke={stroke} viewBox="0 0 24 24">
    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"/>
  </svg>
);

// ChevronDown Icon
export const ChevronDownIcon: React.FC<IconProps> = ({ className = "w-4 h-4", stroke = "currentColor", strokeWidth = 2 }) => (
  <svg className={className} fill="none" stroke={stroke} viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={strokeWidth} d="M19 9l-7 7-7-7" />
  </svg>
);

// ChevronUp Icon
export const ChevronUpIcon: React.FC<IconProps> = ({ className = "w-4 h-4", stroke = "currentColor", strokeWidth = 2 }) => (
  <svg className={className} fill="none" stroke={stroke} viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={strokeWidth} d="M5 15l7-7 7 7" />
  </svg>
);

// Empty State Icon (for "no data" displays)
export const EmptyIcon: React.FC<IconProps> = ({ className = "w-16 h-16", stroke = "currentColor", strokeWidth = 2 }) => (
  <svg className={className} fill="none" stroke={stroke} viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={strokeWidth} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
  </svg>
);

// Spinner/Loading Icon
export const SpinnerIcon: React.FC<IconProps> = ({ className = "w-5 h-5" }) => (
  <svg className={`${className} animate-spin`} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
  </svg>
);

// Volume/Speaker Icon (for speaking state)
export const VolumeIcon: React.FC<IconProps> = ({ className = "w-8 h-8", fill = "currentColor" }) => (
  <svg className={className} fill={fill} viewBox="0 0 24 24">
    <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02z"/>
  </svg>
);

// Alert/Warning Triangle Icon
export const AlertIcon: React.FC<IconProps> = ({ className = "w-8 h-8", fill = "currentColor" }) => (
  <svg className={className} fill={fill} viewBox="0 0 24 24">
    <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z"/>
  </svg>
);

// Refresh/Retry Icon
export const RefreshIcon: React.FC<IconProps> = ({ className = "w-8 h-8", fill = "currentColor" }) => (
  <svg className={className} fill={fill} viewBox="0 0 24 24">
    <path d="M12 6v3l4-4-4-4v3c-4.42 0-8 3.58-8 8 0 1.57.46 3.03 1.24 4.26L6.7 14.8c-.45-.83-.7-1.79-.7-2.8 0-3.31 2.69-6 6-6z"/>
  </svg>
);

// Home Icon
export const HomeIcon: React.FC<IconProps> = ({ className = "w-4 h-4", stroke = "currentColor", strokeWidth = 2 }) => (
  <svg className={className} fill="none" stroke={stroke} viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={strokeWidth} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
  </svg>
);

// Monitor/Display Icon (for API test)
export const MonitorIcon: React.FC<IconProps> = ({ className = "w-6 h-6", stroke = "currentColor", strokeWidth = 2 }) => (
  <svg className={className} fill="none" stroke={stroke} viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={strokeWidth} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
  </svg>
);

// Power/Start Icon (for reading start)
export const PowerIcon: React.FC<IconProps> = ({ className = "w-10 h-10", fill = "currentColor" }) => (
  <svg className={className} fill={fill} viewBox="0 0 24 24">
    <path d="M7.5 1v7h1V1z"/>
    <path d="M3 8.812a5 5 0 0 1 2.578-4.375l-.485-.874A6 6 0 1 0 11 3.616l-.501.865A5 5 0 1 1 3 8.812"/>
  </svg>
);

// Lightning/Bolt Icon (for accuracy/performance)
export const LightningIcon: React.FC<IconProps> = ({ className = "w-6 h-6", stroke = "currentColor", strokeWidth = 2 }) => (
  <svg className={className} fill="none" stroke={stroke} viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={strokeWidth} d="M13 10V3L4 14h7v7l9-11h-7z" />
  </svg>
);

// Check Circle Icon (for success states)
export const CheckCircleIcon: React.FC<IconProps> = ({ className = "w-16 h-16", stroke = "currentColor", strokeWidth = 2 }) => (
  <svg className={className} fill="none" stroke={stroke} viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={strokeWidth} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

// Circle Icon (generic dot/indicator)
export const CircleIcon: React.FC<IconProps> = ({ className = "w-5 h-5", fill = "currentColor" }) => (
  <svg className={className} fill={fill} viewBox="0 0 24 24">
    <circle cx="12" cy="12" r="10"/>
  </svg>
);

// Listening/Microphone Active Icon
export const ListeningIcon: React.FC<IconProps> = ({ className = "w-5 h-5", fill = "currentColor" }) => (
  <svg className={className} fill={fill} viewBox="0 0 24 24">
    <path d="M12 2c1.1 0 2 .9 2 2v6c0 1.1-.9 2-2 2s-2-.9-2-2V4c0-1.1.9-2 2-2zm5.3 5.3l1.4 1.4C20 10.1 21 12 21 14h-2c0-1.4-.6-2.8-1.6-3.7l-1.4-1.4 1.3-1.6zm-10.6 0L8.1 8.7C7.6 9.2 7 10.6 7 12H5c0-2 1-3.9 2.3-5.3L8.7 7.3z"/>
    <path d="M19 14h2c0 2.8-1.9 5.1-4.5 5.8v2.2h-1v-2.2C12.9 19.1 11 16.8 11 14h2c0 1.7 1.3 3 3 3s3-1.3 3-3z"/>
  </svg>
);

// Speaking/Volume Max Icon
export const SpeakingIcon: React.FC<IconProps> = ({ className = "w-5 h-5", fill = "currentColor" }) => (
  <svg className={className} fill={fill} viewBox="0 0 24 24">
    <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/>
  </svg>
);

// Reconnect/Sync Icon
export const ReconnectIcon: React.FC<IconProps> = ({ className = "w-5 h-5", fill = "currentColor" }) => (
  <svg className={className} fill={fill} viewBox="0 0 24 24">
    <path d="M12 4V1L8 5l4 4V6c3.31 0 6 2.69 6 6 0 1.01-.25 1.97-.7 2.8l1.46 1.46C19.54 15.03 20 13.57 20 12c0-4.42-3.58-8-8-8z"/>
    <path d="M6 12c0-3.31 2.69-6 6-6v3l4-4-4-4v3c-4.42 0-8 3.58-8 8 0 1.57.46 3.03 1.24 4.26L6.7 14.8C6.25 13.97 6 13.01 6 12z"/>
  </svg>
);

// Error/Alert Circle Icon
export const ErrorCircleIcon: React.FC<IconProps> = ({ className = "w-8 h-8", fill = "currentColor" }) => (
  <svg className={className} fill={fill} viewBox="0 0 24 24">
    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
  </svg>
);

// Message/Chat Bubble Icon
export const MessageIcon: React.FC<IconProps> = ({ className = "w-16 h-16", stroke = "currentColor", strokeWidth = 2 }) => (
  <svg className={className} fill="none" stroke={stroke} viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={strokeWidth} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
  </svg>
);

// Export all icons as a group
export const Icons = {
  User: UserIcon,
  Assistant: AssistantIcon,
  Chat: ChatIcon,
  Clock: ClockIcon,
  Close: CloseIcon,
  Edit: EditIcon,
  Delete: DeleteIcon,
  Plus: PlusIcon,
  Filter: FilterIcon,
  Search: SearchIcon,
  Microphone: MicrophoneIcon,
  MicrophoneOff: MicrophoneOffIcon,
  Book: BookIcon,
  Student: StudentIcon,
  Teacher: TeacherIcon,
  Activity: ActivityIcon,
  Settings: SettingsIcon,
  Upload: UploadIcon,
  Download: DownloadIcon,
  Checkmark: CheckmarkIcon,
  Warning: WarningIcon,
  Info: InfoIcon,
  ChevronDown: ChevronDownIcon,
  ChevronUp: ChevronUpIcon,
  Empty: EmptyIcon,
  Spinner: SpinnerIcon,
  Volume: VolumeIcon,
  Alert: AlertIcon,
  Refresh: RefreshIcon,
  Home: HomeIcon,
  Monitor: MonitorIcon,
  Power: PowerIcon,
  Lightning: LightningIcon,
  CheckCircle: CheckCircleIcon,
  Circle: CircleIcon,
  Listening: ListeningIcon,
  Speaking: SpeakingIcon,
  Reconnect: ReconnectIcon,
  ErrorCircle: ErrorCircleIcon,
  Message: MessageIcon,
};
